import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Plus } from "lucide-react";

export default function Contracts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", title: "", start_date: "", end_date: "", monthly_commitment_litres: 10000, payment_terms_days: 30, notes: "", rate_slabs: [{ _sid: "S1", from_qty: 0, to_qty: 5000, rate: 95 }, { _sid: "S2", from_qty: 5000, to_qty: 999999, rate: 93.5 }] });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const { data } = useQuery({ queryKey: ["contracts"], queryFn: () => api.get("/contracts").then((r) => r.data) });
  const { data: customers } = useQuery({ queryKey: ["cust-mini"], queryFn: () => api.get("/customers?limit=200").then((r) => r.data) });

  const create = useMutation({
    mutationFn: (p) => api.post("/contracts", p),
    onSuccess: () => { toast.success("Contract created"); setOpen(false); qc.invalidateQueries({ queryKey: ["contracts"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const updateSlab = (idx, key, val) => {
    const slabs = [...form.rate_slabs];
    slabs[idx] = { ...slabs[idx], [key]: Number(val) };
    setForm({ ...form, rate_slabs: slabs });
  };

  return (
    <div className="space-y-4" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Contracts & Rate Agreements</h1>
          <p className="text-sm text-muted-foreground">Multi-tier pricing with volume commitments.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-contract"><Plus size={14} className="mr-1" /> New contract</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New contract</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="ct-customer"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                  <SelectContent>{(customers?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Contract title</Label><Input value={form.title} onChange={set("title")} data-testid="ct-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={set("start_date")} /></div>
                <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={set("end_date")} /></div>
                <div><Label>Monthly commit (L)</Label><Input type="number" value={form.monthly_commitment_litres} onChange={set("monthly_commitment_litres")} /></div>
                <div><Label>Payment terms (days)</Label><Input type="number" value={form.payment_terms_days} onChange={set("payment_terms_days")} /></div>
              </div>
              <div>
                <Label>Rate slabs</Label>
                <div className="mt-1 space-y-2">
                  {form.rate_slabs.map((s, i) => (
                    <div key={s._sid} className="grid grid-cols-3 gap-2">
                      <Input type="number" value={s.from_qty} onChange={(e) => updateSlab(i, "from_qty", e.target.value)} placeholder="From" />
                      <Input type="number" value={s.to_qty} onChange={(e) => updateSlab(i, "to_qty", e.target.value)} placeholder="To" />
                      <Input type="number" step="0.1" value={s.rate} onChange={(e) => updateSlab(i, "rate", e.target.value)} placeholder="Rate ₹/L" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={() => create.mutate(form)} disabled={!form.customer_id || !form.title} data-testid="btn-save-contract">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Monthly (L)</th><th className="px-4 py-3">Terms</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {(data?.items || []).map((c) => (
                <tr key={c.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                  <td className="px-4 py-3">{c.customer_name}</td>
                  <td className="px-4 py-3">{c.title}</td>
                  <td className="px-4 py-3 text-xs">{c.start_date} → {c.end_date}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.monthly_commitment_litres?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-xs">Net {c.payment_terms_days} days</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status === "active" ? "delivered" : c.status === "expired" ? "cancelled" : "pending"} /></td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No contracts yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
